import { useEffect, useRef, useState } from 'react';
import ButtonSecondary from '../../button/button-secondary';
import { ButtonSize } from '../../button/types';
import IconExpand from '../../icons/expand';
import IconDown from '../../icons/down';
import IconUp from '../../icons/up';
import DocumentItem from './document';
import IconLoader from '../../icons/loader';
import ClickOutsideDetector from '../../autocomplete/click-outside-detector';
import { PermissionEnum } from '../../../base-types';
import ButtonPseudo from '../../button/button-pseudo';
import { DocumentsTypes } from './types';

import styles from './documents.module.css';

export default function Documents({
  documents,
  selectedDocumentId,
  onDownloadDocument,
  downloadInProgress,
  setSubPanelVisible,
  setSelectedDocumentName,
  setTransactionSidepanelVisible,
  onUploadDocument,
  uploadDocumentInProgress,
  uploadDocumentIsSuccess,
  uploadDocumentErrors,
  setSelectedDocumentId,
  internal,
  recipientAccount,
  senderAccount,
  activePermission,
  getTransactionDocument,
  documentsIsLoading,
  documentsPagination,
  getTransactionDocuments,
  documentsIsFetching,
  attachedDocumentsErrors,
  uploadedDocumentsErrors,
  glDocuments,
}: DocumentsTypes) {
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedOrganizationId, setSelecteOrganizationId] = useState('');

  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (uploadDocumentIsSuccess) {
      if (uploadInputRef?.current) {
        uploadInputRef.current.value = '';
      }

      if (selectedOrganizationId) {
        setSelecteOrganizationId('');
      }
    }
  }, [uploadDocumentIsSuccess, uploadInputRef]);

  let content;

  if (
    (!glDocuments &&
      !activePermission?.includes(
        PermissionEnum.TRANSACTIONS_DOCUMENTS_LIST,
      )) ||
    (glDocuments &&
      !activePermission?.includes(
        PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DOCUMENTS_LIST,
      ))
  ) {
    content = (
      <div className={styles.emptyDocuments}>
        You don&apos;t have access to this section
      </div>
    );
  } else if (
    (!glDocuments &&
      activePermission?.includes(PermissionEnum.TRANSACTIONS_DOCUMENTS_LIST)) ||
    (glDocuments &&
      activePermission?.includes(
        PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DOCUMENTS_LIST,
      ))
  ) {
    if (documentsIsLoading) {
      content = (
        <div className={styles.contentLoader}>
          <IconLoader />
        </div>
      );
    } else if (!documentsIsLoading && !documents?.length) {
      content = (
        <div className={styles.emptyDocuments}>There are no attachements</div>
      );
    } else if (!documentsIsLoading && documents) {
      content = (
        <div className={styles.documentsList}>
          {documents.map((item) => (
            <DocumentItem
              key={item.id}
              setSubPanelVisible={setSubPanelVisible}
              document={item}
              onDownloadDocument={onDownloadDocument}
              downloadInProgress={Boolean(
                downloadInProgress && selectedDocumentId === item.id,
              )}
              setSelectedDocumentName={setSelectedDocumentName}
              setTransactionSidepanelVisible={setTransactionSidepanelVisible}
              setSelectedDocumentId={setSelectedDocumentId}
              getTransactionDocument={getTransactionDocument}
              internal={internal}
              recipientAccount={recipientAccount}
              senderAccount={senderAccount}
            />
          ))}
          {documentsPagination?.hasNextPage && getTransactionDocuments && (
            <div className={styles.transactionDocumentsControls}>
              <ButtonPseudo
                text={documentsIsFetching ? 'Loading...' : 'Show more'}
                className={styles.transactionDocumentsPaginationButton}
                disabled={documentsIsFetching}
                onClick={() =>
                  getTransactionDocuments(documentsPagination.endCursor)
                }
                bold
              />
            </div>
          )}
        </div>
      );
    }
  }

  let icon = <IconExpand />;

  if (internal) {
    icon = menuVisible ? <IconUp /> : <IconDown />;
  }

  return (
    <div className={styles.documentsWrapper}>
      <div className={styles.documentsTop}>
        <div className={styles.documentsTitle}>Attached documents</div>
        {((!glDocuments &&
          activePermission?.includes(
            PermissionEnum.TRANSACTIONS_DOCUMENTS_LIST,
          ) &&
          activePermission.includes(
            PermissionEnum.TRANSACTIONS_DOCUMENTS_CREATE_ACTION,
          )) ||
          (glDocuments &&
            activePermission?.includes(
              PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DOCUMENTS_LIST,
            ) &&
            activePermission?.includes(
              PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DOCUMENTS_CREATE_ACTION,
            ))) && (
          <div className={styles.fileInputWrapper}>
            {internal && (
              <ClickOutsideDetector setOptionsListVisible={setMenuVisible}>
                <>
                  <ButtonSecondary
                    size={ButtonSize.SMALL}
                    text={
                      uploadDocumentInProgress ? 'Loading...' : 'Add document'
                    }
                    icon={icon}
                    className={styles.addFileButton}
                    disabled={uploadDocumentInProgress}
                    onClick={() => setMenuVisible(!menuVisible)}
                  />
                  {menuVisible && (
                    <ul className={styles.organizationMenu}>
                      <li className={styles.organizationMenuTitle}>
                        Upload document to sender
                      </li>
                      <li>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuVisible(false);
                            setSelecteOrganizationId(
                              senderAccount?.organizationId as string,
                            );

                            if (uploadInputRef && uploadInputRef.current) {
                              uploadInputRef.current.click();
                            }
                          }}
                        >
                          {senderAccount?.organization?.type === 'INDIVIDUAL'
                            ? `${senderAccount?.organization?.firstName} ${senderAccount?.organization?.lastName}`
                            : senderAccount?.organization?.companyName}
                        </button>
                      </li>
                      <li className={styles.organizationMenuTitle}>
                        Upload document to recipient
                      </li>
                      <li>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuVisible(false);
                            setSelecteOrganizationId(
                              recipientAccount?.organizationId as string,
                            );

                            if (uploadInputRef && uploadInputRef.current) {
                              uploadInputRef.current.click();
                            }
                          }}
                        >
                          {recipientAccount?.organization?.type === 'INDIVIDUAL'
                            ? `${recipientAccount?.organization?.firstName} ${recipientAccount?.organization?.lastName}`
                            : recipientAccount?.organization?.companyName}
                        </button>
                      </li>
                    </ul>
                  )}
                </>
              </ClickOutsideDetector>
            )}
            {!internal && (
              <ButtonSecondary
                size={ButtonSize.SMALL}
                text={uploadDocumentInProgress ? 'Loading...' : 'Add document'}
                icon={icon}
                className={styles.addFileButton}
                disabled={uploadDocumentInProgress}
                onClick={() => {
                  if (uploadInputRef && uploadInputRef.current) {
                    uploadInputRef.current.click();
                  }
                }}
              />
            )}
            <input
              type="file"
              className={styles.fileInput}
              ref={uploadInputRef}
              onChange={(e) => {
                if (onUploadDocument) {
                  if (internal) {
                    onUploadDocument(
                      e.target.files as FileList,
                      selectedOrganizationId,
                    );
                  } else {
                    onUploadDocument(e.target.files as FileList);
                  }
                }
              }}
              multiple
            />
          </div>
        )}
      </div>
      {uploadedDocumentsErrors && uploadedDocumentsErrors?.length > 0 && (
        <ul className={styles.uploadDocumentsErrors}>
          {uploadedDocumentsErrors?.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${item.name}-${index}`}>
              - The document <strong>{item.name}</strong> was not uploaded.
              <br />
              <span>
                <strong>Reason:</strong> {item.error}
              </span>
            </li>
          ))}
        </ul>
      )}
      {attachedDocumentsErrors &&
        Object.keys(attachedDocumentsErrors)?.length > 0 && (
          <ul className={styles.uploadDocumentErrors}>
            {Object.keys(attachedDocumentsErrors)?.map((item) => (
              <li key={item}>
                - The document{' '}
                <strong>{attachedDocumentsErrors[item].name}</strong> was not
                uploaded.
                <br />
                <span>
                  <strong>Reason:</strong> {attachedDocumentsErrors[item].error}
                </span>
              </li>
            ))}
          </ul>
        )}
      {uploadDocumentErrors && uploadDocumentErrors.length > 0 && (
        <ul className={styles.uploadDocumentErrors}>
          {uploadDocumentErrors.map((item) => (
            <li key={item}>{`- ${item}`}</li>
          ))}
        </ul>
      )}
      <div className={styles.documentsItemsList}>{content}</div>
    </div>
  );
}
